import { defineQuery, useQuery, useQueryCache } from "@pinia/colada";

const cartParsed = v.safeParse(cartSchema, cart);
const customerDetailsDefault = { first_name: "", last_name: "", company: "", email: "", phone: "", address_1: "", address_2: "", city: "", state: "", postcode: "", country: "" };

export const useCart = defineQuery(() => {
  const { data: cart, isLoading, status, error } = useQuery<Cart, Error, Cart>({
    key: () => ["cart"],
    query: async () => v.parse(cartSchema, await ajaxRequest("get_cart")),
    initialData: cartParsed.success ? () => cartParsed.output : undefined,
    enabled: !cartParsed.success,
    placeholderData: {
      customer: {
        billing: { prefix: "billing", ...customerDetailsDefault },
        shipping: { prefix: "shipping", ...customerDetailsDefault },
      },
      cart_items: [],
      goals: [],
      shipping: { packages: [] },
      lillopoints: { is_applied: false, conversion_rate: 500, max_points: 0, total_points: 0 },
      totals: { subtotal: 0, coupons: {}, fees: {}, taxes: {}, shipping: 0, total: 0 },
    },
  });
  return { cart, isLoading, status, error };
});

export function useAddToCart() {
  const queryCache = useQueryCache();
  const { mutate: onAddToCart, ...rest } = useMutationHandler<{ cart: Cart }, AddToCart>({
    action: "add_to_cart",
    icons: { pending: "i-local-cart" },
    resetDelay: 1000,
    onSuccess: ({ cart }) => queryCache.setQueryData(["cart"], cart),
  });
  return { onAddToCart, ...rest };
}

export function useRemoveFromCart() {
  const queryCache = useQueryCache();
  const { mutate: onRemoveFromCart, ...rest } = useMutationHandler<{ cart: Cart }, RemoveFromCart>({
    action: "remove_from_cart",
    icons: { pending: "i-local-remove" },
    onSuccess: ({ cart }) => queryCache.setQueryData(["cart"], cart),
  });
  return { onRemoveFromCart, ...rest };
}

export function useUpdateCartItem() {
  const queryCache = useQueryCache();
  const { mutate: onUpdateCartItem, ...rest } = useMutationHandler<{ cart: Cart }, UpdateCartItem>({
    action: "update_cart_item",
    onSuccess: ({ cart }) => queryCache.setQueryData(["cart"], cart),
  });
  return { onUpdateCartItem, ...rest };
}

export function useApplyCouponForm() {
  const queryCache = useQueryCache();
  return useFormHandler<{ message: string; cart: Cart }, ApplyCoupon>({
    schema: applyCouponSchema,
    action: "apply_coupon",
    onSuccess: ({ cart }) => queryCache.setQueryData(["cart"], cart),
  });
}

export function useRemoveCoupon() {
  const queryCache = useQueryCache();
  const { mutate: onRemoveCoupon, ...rest } = useMutationHandler<{ cart: Cart }, RemoveCoupon>({
    action: "remove_coupon",
    icons: { pending: "i-local-remove" },
    onSuccess: ({ cart }) => queryCache.setQueryData(["cart"], cart),
  });
  return { onRemoveCoupon, ...rest };
}

export function useChooseShippingMethod() {
  const queryCache = useQueryCache();
  const { mutate: onChooseShippingMethod, ...rest } = useMutationHandler<{ cart: Cart }, ChooseShippingMethod>({
    action: "choose_shipping_method",
    icons: { pending: "i-local-check" },
    onSuccess: ({ cart }) => queryCache.setQueryData(["cart"], cart),
  });
  return { onChooseShippingMethod, ...rest };
}

export function useApplyLillopointsForm() {
  const queryCache = useQueryCache();
  const { cart } = useCart();
  return useFormHandler<{ message: string; cart: Cart }, ApplyLillopoints>({
    schema: applyLillopointsSchema,
    action: "apply_lillopoints",
    initialValues: { points: cart.value.lillopoints.total_points },
    onSuccess: ({ cart }) => queryCache.setQueryData(["cart"], cart),
  });
}
